import React, { useState, useRef, useEffect } from 'react';
import { FaCog, FaTimes, FaPalette, FaUserFriends, FaTasks, FaCalendarAlt, FaSave, FaPrint, FaDownload, FaSignOutAlt } from 'react-icons/fa';
import { useViewMode } from '../../hooks/useViewMode.jsx';
import { useTranslation } from '../../utils/translations';
import { useLanguage } from '../../context/LanguageContext';
import { printData } from '../../utils/exportUtils';
import PropTypes from 'prop-types';
import './SettingsPanel.css';

import { generatePdf } from '../../utils/pdfExport';
import { saveAs } from 'file-saver';

import { transformDataForReport } from '../../utils/reportUtils';

const SettingsPanel = ({ onSignOut, data }) => {
  const [isOpen, setIsOpen] = useState(false);
  const { viewMode, setViewMode } = useViewMode();
  const { language } = useLanguage();
  const { t } = useTranslation();
  const fabRef = useRef(null);

  // Prevent scroll events on the FAB from interfering with main content (Mobile focus)
  useEffect(() => {
    const fabElement = fabRef.current;
    if (!fabElement) return;

    const handleTouchStart = (e) => {
      // Store initial touch position
      fabElement.startY = e.touches[0].clientY;
    };

    const handleTouchMove = (e) => {
      // Calculate touch movement
      const currentY = e.touches[0].clientY;
      const deltaY = fabElement.startY - currentY;
      
      // Prevent default behavior on the FAB
      e.preventDefault();
      
      // Pass scroll to main container
      const mainContainer = document.querySelector('.data-view-main');
      if (mainContainer) {
        mainContainer.scrollTop += deltaY * 2; // Multiply for better scroll sensitivity
      }
      
      // Update start position for next move
      fabElement.startY = currentY;
    };

    // Only add touch events for mobile
    fabElement.addEventListener('touchstart', handleTouchStart, { passive: false });
    fabElement.addEventListener('touchmove', handleTouchMove, { passive: false });

    return () => {
      fabElement.removeEventListener('touchstart', handleTouchStart);
      fabElement.removeEventListener('touchmove', handleTouchMove);
    };
  }, []);

  const togglePanel = () => setIsOpen(!isOpen);

  const handleViewChange = (mode) => {
    setViewMode(mode);
    setIsOpen(false);
  };

  const generateFileName = (familyData) => {
    // Get current date in Month-Day-Year format
    const now = new Date();
    const month = String(now.getMonth() + 1).padStart(2, '0'); // 01-12
    const day = String(now.getDate()).padStart(2, '0'); // 01-31
    const year = now.getFullYear(); // 2025
    const date = `${month}-${day}-${year}`; // 09-07-2025
    
    // Get family name from first parent's last name
    let familyName = 'Family';
    if (familyData.parentName1) {
      const lastName = familyData.parentName1.split(' ').pop();
      familyName = lastName;
    }
    
    return `${familyName}-Church-Activities-${date}.pdf`;
  };

  const handleExport = async () => {
    if (!data) {
      console.error("No data available to generate PDF.");
      return;
    }
    const transformedData = transformDataForReport(data);
    if (!transformedData) {
      console.error("Data transformation failed.");
      return;
    }
    const blob = await generatePdf(transformedData, viewMode, language);
    
    // Generate the filename
    const fileName = generateFileName(data.familyData);
    
    // Detect iOS Safari
    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
    const isSafari = /Safari/.test(navigator.userAgent) && /Apple Computer/.test(navigator.vendor);
    
    if (isIOS && navigator.share) {
      // For iOS with Web Share API (iOS 12+) - This triggers the native share menu
      try {
        const file = new File([blob], fileName, { type: 'application/pdf' });
        await navigator.share({
          title: 'Activity Report',
          text: 'Family activity report',
          files: [file]
        });
        setIsOpen(false);
        return;
      } catch (error) {
        console.log('Native share failed, trying fallback:', error);
      }
    }
    
    if (isIOS || isSafari) {
      // For iOS without Web Share API or when share fails
      // Create a data URL and open in new window - this allows iOS user to share/save
      const url = URL.createObjectURL(blob);
      
      // For iOS, opening in a new window triggers Safari's share button
      const newWindow = window.open(url, '_blank');
      
      // Fallback if popup blocked - use invisible link
      if (!newWindow) {
        const link = document.createElement('a');
        link.href = url;
        link.download = fileName;
        link.style.display = 'none';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      }
      
      // Clean up the URL after a delay
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } else {
      // For desktop and Android, use file-saver
      saveAs(blob, fileName);
    }
    
    setIsOpen(false);
  };

  const handlePrint = async () => {
    if (!data) {
      console.error("No data available to generate PDF.");
      return;
    }
    const transformedData = transformDataForReport(data);
    if (!transformedData) {
      console.error("Data transformation failed.");
      return;
    }
    const blob = await generatePdf(transformedData, viewMode, language);
    const url = URL.createObjectURL(blob);
    const iframe = document.createElement('iframe');
    iframe.style.display = 'none';
    iframe.src = url;
    document.body.appendChild(iframe);
    iframe.contentWindow.print();
  };

  const handleSignOut = () => {
    setIsOpen(false);
    if (onSignOut) {
      onSignOut();
    }
  };

  return (
    <>
      <button 
        ref={fabRef}
        className="settings-fab" 
        onClick={togglePanel} 
        aria-label="Open settings"
      >
        <FaCog />
      </button>
      <div className={`settings-panel-overlay ${isOpen ? 'open' : ''}`} onClick={togglePanel}></div>
      <div className={`settings-panel ${isOpen ? 'open' : ''}`}>
        <div className="settings-header">
          <h3>Settings</h3>
          <button className="close-button" onClick={togglePanel} aria-label="Close settings">
            <FaTimes />
          </button>
        </div>
        <div className="settings-content">
          <div className="setting-section">
            <h4><FaPalette /> {t('main.viewSettings')}</h4>
            <p>Select how to group the data.</p>
            <div className="toggle-switch">
              <button
                className={`toggle-option ${viewMode === 'student' ? 'active' : ''}`}
                onClick={() => handleViewChange('student')}
              >
                <FaUserFriends /> {t('main.studentView')}
              </button>
              <button
                className={`toggle-option ${viewMode === 'activity' ? 'active' : ''}`}
                onClick={() => handleViewChange('activity')}
              >
                <FaTasks /> {t('main.activityView')}
              </button>
              <button
                className={`toggle-option ${viewMode === 'date' ? 'active' : ''}`}
                onClick={() => handleViewChange('date')}
              >
                <FaCalendarAlt /> {t('main.dateView')}
              </button>
            </div>
          </div>
          <div className="setting-section">
            <h4><FaSave /> {t('main.exportOptions')}</h4>
            <p>Save the current view for sharing or printing.</p>
            <div className="export-buttons">
              <button className="export-button" onClick={handleExport}>
                <FaDownload /> {t('main.export')}
              </button>
              <button className="export-button" onClick={handlePrint}>
                <FaPrint /> {t('main.print')}
              </button>
            </div>
          </div>
          {onSignOut && (
            <div className="sign-out-section">
              <button className="sign-out-button" onClick={handleSignOut}>
                <FaSignOutAlt /> {t('main.signOut')}
              </button>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

SettingsPanel.propTypes = {
  onSignOut: PropTypes.func,
  data: PropTypes.object,
};

export default SettingsPanel;
